import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { theme } from "antd";
import Quill from "quill";
import { useId, useLayoutEffect, useRef, useState } from "react";
import "./Editor.scss";

export interface EditorProps {
  readonly className?: string;
  readonly inline?: boolean;
  readonly readOnly?: boolean;
  readonly rows?: number | "auto";
  readonly bordered?: boolean;
  readonly placeholder?: string;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly onBlur?: (value: string) => void;
  readonly onFocus?: (value: string) => void;
}

export function Editor(props: EditorProps) {
  const ref = useRef<HTMLDivElement>(null!);
  const quillRef = useRef(null);
  const [editor, setEditor] = useState<Quill | null>(null);
  const { token } = theme.useToken();

  // Lifecycle

  useLayoutEffect(() => {
    if (quillRef.current) return;

    const editor = new Quill(ref.current, {
      // debug: "info",
      placeholder: props.placeholder,
      modules: {
        toolbar: !props.inline && !props.readOnly,
        keyboard: {
          bindings: {
            enter: {
              key: 13,
              handler: () => {
                return !props.inline;
              },
            },
          },
        },
        clipboard: {
          newLines: !props.inline,
        },
      },
      theme: "snow",
    });
    (quillRef as any).current = editor;
    setEditor(editor);

    editor.clipboard.dangerouslyPasteHTML(props.value ?? "");

    return () => {
      quillRef.current = null;
      setEditor(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (editor) {
      const html = getHtml();
      if (props.value !== html) {
        editor.clipboard.dangerouslyPasteHTML(props.value ?? "");
      }
    }
  }, [editor, props.value]);

  useLayoutEffect(() => {
    const onChange = () => props.onChange!(getHtml());
    const onBlur = () => props.onBlur!(getHtml());
    const onFocus = () => props.onFocus!(getHtml());

    props.onChange && editor?.on("text-change", onChange);
    props.onFocus && editor?.root.addEventListener("focus", onFocus);
    props.onBlur && editor?.root.addEventListener("blur", onBlur);

    return () => {
      props.onChange && editor?.off("text-change", onChange);
      props.onFocus && editor?.root.removeEventListener("focus", onFocus);
      props.onBlur && editor?.root.removeEventListener("blur", onBlur);
    };
  });

  // Methods

  function getHtml(): string {
    const html =
      (editor?.getLength() || 0) > 1 ? editor?.root.innerHTML || "" : "";

    return html;
  }

  return (
    <div
      className={`my-editor flex flex-col overflow-hidden ${props.inline ? "my-inline-editor-readonly" : ""} ${props.className}`}
      style={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor:
          !props.inline && !props.readOnly
            ? token.colorPrimaryBorder
            : "transparent",
        borderRadius: token.borderRadius,
        pointerEvents: props.readOnly ? "none" : "initial",
      }}
    >
      <div
        ref={ref}
        style={{
          minHeight:
            typeof props.rows === "number"
              ? 16 * (props.rows || 1)
              : props.rows,
        }}
      ></div>
    </div>
  );
}

export default Editor;
